import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useRouteError,
} from "react-router-dom";
import { lazy, Suspense, type ReactNode } from "react";

import { env } from "@/config/env";
import { MarcusAutopilotOverlay } from "@/features/ads/components/marcus/MarcusAutopilotOverlay";

// ── Layouts ──────────────────────────────────────────────────────────────────
import { RootLayout } from "@/app/layouts/RootLayout";
import { ProtectedRoute } from "@/app/ProtectedRoute";
import MarketingLayout from "@/pages/(marketing)/layout";
import AppLayout from "@/pages/(app)/layout";
import SettingsLayout from "@/pages/(app)/settings/layout";
import AdminLayout from "@/pages/(app)/settings/admin/layout";
import PricingLayout from "@/pages/(marketing)/pricing/layout";

// ── Marketing Pages ──────────────────────────────────────────────────────────
const HomePage = lazy(() => import("@/pages/(marketing)/page"));
const PricingPage = lazy(() => import("@/pages/(marketing)/pricing/page"));
const B2BPage = lazy(() => import("@/pages/(marketing)/b2b/page"));
const FreelancerPage = lazy(() => import("@/pages/(marketing)/freelancer/page"));
const PrivacyPage = lazy(() => import("@/pages/(marketing)/privacy/page"));
const TermsPage = lazy(() => import("@/pages/(marketing)/terms/page"));
const CapabilityAdCloningPage = lazy(
  () => import("@/pages/(marketing)/capabilities/ad-cloning/page"),
);
const CapabilityCampaignManagementPage = lazy(
  () => import("@/pages/(marketing)/capabilities/campaign-management/page"),
);
const CapabilityCreativeStudioPage = lazy(
  () => import("@/pages/(marketing)/capabilities/creative-studio/page"),
);
const LpBrandDesignPage = lazy(() => import("@/pages/(marketing)/lp/brand-design/page"));
const LpGeoAuditPage = lazy(() => import("@/pages/(marketing)/lp/geo-audit/page"));

// ── Onboarding ───────────────────────────────────────────────────────────────
const OnboardingPage = lazy(() => import("@/pages/onboarding/page"));

// ── App Pages ────────────────────────────────────────────────────────────────
const OrganizationSettingsPage = lazy(() => import("@/pages/(app)/settings/organization/page"));
const TeamSettingsPage = lazy(() => import("@/pages/(app)/settings/team/page"));

// Department hub/list/team pages are deprecated — all `/departments/*` and
// `/workspaces/:ws/departments/*` URLs now redirect to canonical
// non-departments destinations via `WorkspaceDepartmentRedirect` /
// `DepartmentLegacyRedirect`. Content Studio detail pages remain mounted at
// their existing URLs so deep links continue to resolve.

// Content Studio detail pages
const ContentStudioSourcePage = lazy(
  () => import("@/pages/(app)/departments/marketing/content/source/[sourceId]/page"),
);
const ContentStudioArticlePage = lazy(
  () => import("@/pages/(app)/departments/marketing/content/article/[articleId]/page"),
);
const BrandsPage = lazy(() => import("@/pages/(app)/brands/page"));
const BrandDeckPage = lazy(() => import("@/pages/(app)/brands/[brandId]/deck/page"));

const WorkspacesPage = lazy(() => import("@/pages/(app)/workspaces/page"));

const ArtifactDetailPage = lazy(() => import("@/pages/(app)/artifacts/[id]/page"));

// ── Settings Pages ───────────────────────────────────────────────────────────
const SettingsProfilePage = lazy(() => import("@/pages/(app)/settings/profile/page"));
const SettingsGeneralPage = lazy(() => import("@/pages/(app)/settings/general/page"));
const AdminPage = lazy(() => import("@/pages/(app)/settings/admin/page"));
const AdminPipelineInspectorPage = lazy(
  () => import("@/pages/(app)/settings/admin/pipeline-inspector/page"),
);
const AdminTtsVoicesPage = lazy(() => import("@/pages/(app)/settings/admin/tts-voices/page"));
const AgentLogsPage = lazy(() => import("@/pages/(app)/settings/agent-logs/page"));
const AgentLogDetailPage = lazy(() => import("@/pages/(app)/settings/agent-logs/[chatId]/page"));
const AgentLogsPipelinesPage = lazy(
  () => import("@/pages/(app)/settings/agent-logs/pipelines/page"),
);

// ── Agent Console (Phase 3.5) — staff-only, separate tree under /console/* ──
// Phase 26 — `/console/*` runs OUTSIDE `AppLayout` inside the Aigentise shell
// (full-bleed brand surface; own sidebar; scoped palette). See
// `docs/architecture/portable-runtime/phase-26-report.md` §5.1.
const AigentiseConsoleMount = lazy(() =>
  import("@/components/console/AigentiseConsoleMount").then((m) => ({
    default: m.AigentiseConsoleMount,
  })),
);
const ConsoleLayout = lazy(() => import("@/pages/console/layout"));
const ConsoleIndexPage = lazy(() => import("@/pages/console/page"));
const ConsoleAgentsListPage = lazy(() => import("@/pages/console/agents/page"));
const ConsoleAgentDetailPage = lazy(() => import("@/pages/console/agents/[agentName]/page"));
const ConsoleEditAgentPage = lazy(() => import("@/pages/console/agents/[agentName]/edit/page"));
// Phase 24-2c — Workbench page (merged Playground + Edit Agent surface). Lives
// alongside the legacy pages until 24-8 consolidates routes.
const ConsoleWorkbenchPage = lazy(
  () => import("@/pages/console/agents/[agentName]/workbench/page"),
);
const ConsoleNewAgentPage = lazy(() => import("@/pages/console/agents/new/page"));
const ConsoleVersionPinnedPlaygroundPage = lazy(
  () => import("@/pages/console/playground/[agentName]/[versionId]/page"),
);
const ConsoleUnitsPage = lazy(() => import("@/pages/console/units/page"));
const ConsoleUnitDetailPage = lazy(() => import("@/pages/console/units/[unitName]/page"));
const ConsolePlaygroundLandingPage = lazy(() => import("@/pages/console/playground/page"));
const ConsoleAgentPlaygroundPage = lazy(
  () => import("@/pages/console/playground/[agentName]/page"),
);
const ConsoleSessionsListPage = lazy(() => import("@/pages/console/sessions/page"));
const ConsoleTriggersListPage = lazy(() => import("@/pages/console/triggers/page"));
const ConsoleNewTriggerPage = lazy(() => import("@/pages/console/triggers/new/page"));
const ConsoleTriggerDetailPage = lazy(() => import("@/pages/console/triggers/[triggerId]/page"));
const ConsoleSafetyPage = lazy(() => import("@/pages/console/safety/page"));
// Phase 13-4 / 13-5 — tool playground.
const ConsoleToolsListPage = lazy(() => import("@/pages/console/tools/page"));
// Phase 14-6 — datasets list + detail.
const ConsoleDatasetsListPage = lazy(() => import("@/pages/console/datasets/page"));
const ConsoleDatasetDetailPage = lazy(() => import("@/pages/console/datasets/[datasetId]/page"));
// Phase 14-7 — evaluation run detail (score grid + drill-down).
const ConsoleEvaluationDetailPage = lazy(() => import("@/pages/console/evaluations/[runId]/page"));
const ConsoleToolDetailPage = lazy(() => import("@/pages/console/tools/[toolName]/page"));
const ConsoleSessionDetailPage = lazy(() => import("@/pages/console/sessions/[sessionId]/page"));
// Phase 19-5 — memory + knowledge-base console pages.
const ConsoleMemoryPage = lazy(() => import("@/pages/console/memory/page"));
const ConsoleKnowledgeBasesPage = lazy(() => import("@/pages/console/knowledge-bases/page"));

// ── Legacy Redirects ─────────────────────────────────────────────────────────
// All legacy URL redirects live in src/app/routes/legacy.tsx. Prune as
// analytics confirm zero traffic per route.
import {
  CoAgentSlugRedirect,
  AgentDetailRedirect,
  DepartmentLegacyRedirect,
  WorkspaceDepartmentRedirect,
  ShortDepartmentRedirect,
  ShortTeamRedirect,
  LegacySessionRedirect,
  WorkspaceProductsRedirect,
} from "@/app/routes/legacy";

// ── V2 nav canonical pages ──────────────────────────────────────────────────
const ProductsPage = lazy(() => import("@/pages/(app)/products/page"));
const ExplorePage = lazy(() => import("@/pages/(app)/explore/page"));
const BrandEditorPage = lazy(() => import("@/features/brand/components/BrandEditorPage"));
const AdsWorkspace = lazy(() => import("@/features/ads/components/AdsWorkspace"));
const StaticAdsPage = lazy(() => import("@/features/ads/components/StaticAdsPage"));
const VideoAdsPage = lazy(() => import("@/features/ads/components/VideoAdsPage"));
const CloneImageAdsPage = lazy(() => import("@/features/ads/components/CloneImageAdsPage"));
const CloneVideoAdsPage = lazy(() => import("@/features/ads/components/CloneVideoAdsPage"));
const VideoCanvasPage = lazy(() =>
  import("@/features/video-canvas").then((m) => ({ default: m.VideoCanvasPage })),
);
const UgcBuilderPage = lazy(() => import("@/features/ads/components/UgcBuilderPage"));
const UgcVideosPage = lazy(() => import("@/features/ads/components/UgcVideosPage"));
const VideoTemplatesPage = lazy(() => import("@/features/ads/components/VideoTemplatesPage"));
const UgcFactoryPage = lazy(() => import("@/features/ads/components/UgcFactoryPage"));
const CinematicVideoPage = lazy(() => import("@/features/ads/components/CinematicVideoPage"));
const MusicLibraryPage = lazy(() => import("@/features/ads/components/MusicLibraryPage"));
const TtsVideoPage = lazy(() => import("@/features/ads/components/TtsVideoPage"));
const QuickCheckInPage = lazy(() => import("@/features/ads/components/QuickCheckInPage"));
const AnimateImagePage = lazy(() => import("@/features/ads/components/AnimateImagePage"));
const TalkingAvatarPage = lazy(() => import("@/features/ads/components/TalkingAvatarPage"));
const LipsyncLayout = lazy(() => import("@/features/ads/components/lipsync/LipsyncLayout"));
const AvatarsLibraryPage = lazy(() => import("@/features/ads/components/AvatarsLibraryPage"));
const InspirationsPage = lazy(() => import("@/features/ads/components/InspirationsPage"));
const AdsEditorPage = lazy(() => import("@/features/ads/editor/EditorPage"));
const ZeelyProjectsPage = lazy(() => import("@/features/ads/components/ProjectsPage"));
const ZeelyBrandDnaPage = lazy(() => import("@/features/ads/components/ZeelyBrandDnaPage"));
const ZeelyProductCreatePage = lazy(
  () => import("@/features/ads/components/ZeelyProductCreatePage"),
);
const ZeelyAssetsPage = lazy(() => import("@/features/ads/components/ZeelyAssetsPage"));
const UploadsLibraryPage = lazy(() => import("@/features/ads/components/UploadsLibraryPage"));
const VideoEditorPage = lazy(() =>
  import("@/features/video-editor").then((m) => ({ default: m.VideoEditorPage })),
);
const PaidCampaignsPage = lazy(() => import("@/features/ads/components/PaidCampaignsPage"));
const GoogleCampaignsPage = lazy(() => import("@/features/ads/components/GoogleCampaignsPage"));
const SocialStudioWorkspace = lazy(
  () => import("@/components/social-studio/SocialStudioWorkspace"),
);

// ── Other Pages ──────────────────────────────────────────────────────────────
const AgentsPreviewPage = lazy(() => import("@/pages/agents-preview/page"));
const LogoutPage = lazy(() => import("@/pages/logout/page"));
const GoogleOAuthCallbackPage = lazy(() => import("@/pages/auth/google/callback/page"));

// ── Fallback ─────────────────────────────────────────────────────────────────
function PageLoading() {
  return (
    <div className="flex h-full w-full items-center justify-center p-6">
      <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
    </div>
  );
}

function AdsAuralisScope({ children }: { children: ReactNode }) {
  return (
    <div className="ads-auralis h-full min-h-0 w-full">
      {children}
      <MarcusAutopilotOverlay />
    </div>
  );
}

const adsPage = (element: ReactNode) => <AdsAuralisScope>{element}</AdsAuralisScope>;

function AssetLibraryRedirect() {
  const { hash, search } = useLocation();
  return <Navigate to={{ pathname: "/projects", search, hash }} replace />;
}

function PageError() {
  const error = useRouteError();
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : ((error as { statusText?: string; message?: string })?.statusText ??
          (error as { message?: string })?.message ??
          "Unknown error");
  const stack = error instanceof Error ? error.stack : undefined;
  // Always log to console so the failure is debuggable from devtools even
  // when the user reads the friendlier UI message.


  console.error("[PageError]", error);
  const onCopy = () => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      const text = `${message}\n\n${stack ?? ""}`.trim();
      navigator.clipboard.writeText(text).catch(() => {});
    }
  };
  return (
    <div className="flex flex-col h-full w-full items-center justify-center p-6 gap-4 text-center">
      <h2 className="ds-section-title font-bold text-red-500">Something went wrong!</h2>
      <p className="ds-body-sm text-muted-foreground max-w-2xl break-words">{message}</p>
      {stack && (
        <details className="max-w-3xl w-full text-left">
          <summary className="ds-caption text-muted-foreground cursor-pointer hover:text-foreground">
            Show stack trace
          </summary>
          <pre className="mt-2 p-3 ds-micro bg-muted/40 rounded border border-border/40 overflow-auto max-h-[40vh] whitespace-pre-wrap break-all">
            {stack}
          </pre>
        </details>
      )}
      <div className="flex gap-2">
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 border rounded hover:bg-muted"
        >
          Try again
        </button>
        <button onClick={onCopy} className="px-4 py-2 border rounded hover:bg-muted ds-caption">
          Copy error
        </button>
      </div>
    </div>
  );
}

// ── Legacy redirects ─────────────────────────────────────────────────────────
// Every legacy URL redirect lives in src/app/routes/legacy.tsx. Prune as
// analytics confirm zero traffic per route.

// ── Router ───────────────────────────────────────────────────────────────────
export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <PageError />,
    children: [
      // ── Legacy redirects ───────────────────────────────────────────────
      { path: "/login", element: <Navigate to="/" replace /> },
      { path: "/signup", element: <Navigate to="/" replace /> },
      { path: "/hire", element: <Navigate to="/" replace /> },
      { path: "/geminitwo", element: <Navigate to="/" replace /> },
      { path: "/test", element: <Navigate to="/" replace /> },
      { path: "/testid", element: <Navigate to="/" replace /> },

      // ── Marketing routes ───────────────────────────────────────────────
      {
        element: (
          <MarketingLayout>
            <Outlet />
          </MarketingLayout>
        ),
        children: [
          { index: true, element: <HomePage /> },
          {
            path: "pricing",
            element: (
              <PricingLayout>
                <Outlet />
              </PricingLayout>
            ),
            children: [{ index: true, element: <PricingPage /> }],
          },
          { path: "b2b", element: <B2BPage /> },
          { path: "freelancer", element: <FreelancerPage /> },
          { path: "privacy", element: <PrivacyPage /> },
          { path: "terms", element: <TermsPage /> },
          { path: "capabilities/ad-cloning", element: <CapabilityAdCloningPage /> },
          {
            path: "capabilities/campaign-management",
            element: <CapabilityCampaignManagementPage />,
          },
          { path: "capabilities/creative-studio", element: <CapabilityCreativeStudioPage /> },
          { path: "lp/brand-design", element: <LpBrandDesignPage /> },
          { path: "lp/geo-audit", element: <LpGeoAuditPage /> },
        ],
      },

      // ── Onboarding (no sidebar) ────────────────────────────────────────
      { path: "onboarding", element: <OnboardingPage /> },
      { path: "onboarding/create-organization", element: <OnboardingPage /> },

      // ── App routes (protected) ─────────────────────────────────────────
      {
        element: (
          <ProtectedRoute>
            <AppLayout>
              <Outlet />
            </AppLayout>
          </ProtectedRoute>
        ),
        children: [
          { path: "workspaces", element: <WorkspacesPage /> },
          { path: "workspaces/:workspaceId/products", element: <WorkspaceProductsRedirect /> },
          { path: "artifacts", element: <Navigate to="/workspaces" replace /> },
          // ── Canonical short URLs ─────────────────────────────────────
          { path: "explore", element: <ExplorePage /> },
          { path: "artifacts/:id", element: <ArtifactDetailPage /> },
          {
            path: "ads",
            element: env.adsStudioEnabled ? <AdsWorkspace /> : <Navigate to="/" replace />,
          },
          { path: "static-ads", element: adsPage(<StaticAdsPage />) },
          { path: "static-ads/adclone", element: adsPage(<CloneImageAdsPage />) },
          { path: "video-ads", element: adsPage(<VideoAdsPage />) },
          { path: "video-ads/adclone", element: adsPage(<CloneVideoAdsPage />) },
          { path: "video-ads/ugc-videos", element: adsPage(<UgcVideosPage />) },
          { path: "video-ads/templates", element: adsPage(<VideoTemplatesPage />) },
          { path: "video-ads/canvas", element: adsPage(<VideoCanvasPage />) },
          { path: "video-ads/ugc/builder", element: adsPage(<UgcBuilderPage />) },
          { path: "video-ads/ugc/factory", element: adsPage(<UgcFactoryPage />) },
          { path: "video-ads/cinematic", element: adsPage(<CinematicVideoPage />) },
          { path: "video-ads/music", element: adsPage(<MusicLibraryPage />) },
          { path: "video-ads/tts-video", element: adsPage(<TtsVideoPage />) },
          { path: "video-ads/quick-check-in", element: adsPage(<QuickCheckInPage />) },
          { path: "video-ads/animate", element: adsPage(<AnimateImagePage />) },
          { path: "video-ads/edit/:artifactId", element: adsPage(<AdsEditorPage />) },
          { path: "video-ads/avatars", element: adsPage(<AvatarsLibraryPage />) },
          { path: "video-ads/inspirations", element: adsPage(<InspirationsPage />) },
          { path: "projects", element: <ZeelyProjectsPage /> },
          { path: "brand-dna", element: adsPage(<ZeelyBrandDnaPage />) },
          { path: "video-ads/assets", element: adsPage(<ZeelyAssetsPage />) },
          { path: "video-ads/assets/media", element: adsPage(<UploadsLibraryPage />) },
          { path: "videos/:projectId/edit", element: <VideoEditorPage /> },
          { path: "asset-library", element: <AssetLibraryRedirect /> },
          { path: "paid-campaigns", element: adsPage(<PaidCampaignsPage />) },
          { path: "google-campaigns", element: adsPage(<GoogleCampaignsPage />) },
          { path: "organic-campaigns", element: <SocialStudioWorkspace /> },

          // ── Workspace-scoped department routes (deprecated → redirect) ──
          // The department hub/list/team pages no longer have a UI surface.
          // Inner functional Content Studio pages stay mounted at their
          // existing URLs so deep links keep resolving.
          { path: "workspaces/:wsSlug/departments", element: <WorkspaceDepartmentRedirect /> },
          {
            path: "workspaces/:wsSlug/departments/:dept",
            element: <WorkspaceDepartmentRedirect />,
          },
          {
            path: "workspaces/:wsSlug/departments/marketing/content/source/:sourceId",
            element: <ContentStudioSourcePage />,
          },
          {
            path: "workspaces/:wsSlug/departments/marketing/content/article/:articleId",
            element: <ContentStudioArticlePage />,
          },
          {
            path: "workspaces/:wsSlug/departments/:dept/:teamSlug",
            element: <WorkspaceDepartmentRedirect />,
          },
          {
            path: "workspaces/:wsSlug/departments/businessops/forge/deck",
            element: <WorkspaceDepartmentRedirect />,
          },
          {
            path: "workspaces/:wsSlug/departments/strategy/forge/deck",
            element: <WorkspaceDepartmentRedirect />,
          },

          // ── Legacy department routes → redirect to workspace-scoped ──
          { path: "departments", element: <DepartmentLegacyRedirect /> },
          { path: "departments/:dept", element: <DepartmentLegacyRedirect /> },
          {
            path: "departments/marketing/content/source/:sourceId",
            element: <DepartmentLegacyRedirect />,
          },
          {
            path: "departments/marketing/content/article/:articleId",
            element: <DepartmentLegacyRedirect />,
          },
          { path: "departments/:dept/:teamSlug", element: <DepartmentLegacyRedirect /> },
          { path: "departments/businessops/forge/deck", element: <DepartmentLegacyRedirect /> },
          { path: "departments/strategy/forge/deck", element: <DepartmentLegacyRedirect /> },

          // ── Short department aliases (/creative/* -> workspace-scoped) ──
          { path: "marketing", element: <ShortDepartmentRedirect dept="marketing" /> },
          { path: "marketing/:teamSlug", element: <ShortTeamRedirect dept="marketing" /> },
          { path: "creative", element: <ShortDepartmentRedirect dept="creative" /> },
          { path: "creative/:teamSlug", element: <ShortTeamRedirect dept="creative" /> },
          { path: "businessops", element: <ShortDepartmentRedirect dept="businessops" /> },
          { path: "businessops/:teamSlug", element: <ShortTeamRedirect dept="businessops" /> },

          // ── Legacy coagent redirects ─────────────────────────────────
          { path: "coagent", element: <DepartmentLegacyRedirect /> },
          { path: "coagent/agent/:agentId", element: <AgentDetailRedirect /> },
          { path: "coagent/forge/deck", element: <DepartmentLegacyRedirect /> },
          { path: "coagent/:agentSlug", element: <CoAgentSlugRedirect /> },

          // ── Other legacy redirects ───────────────────────────────────
          {
            path: "business-ops",
            element: <ShortDepartmentRedirect dept="businessops" />,
          },
          { path: "brands", element: <BrandsPage /> },
          { path: "brands/:brandId", element: <BrandEditorPage /> },
          { path: "brands/:brandId/deck", element: <BrandDeckPage /> },
          { path: "products", element: <ProductsPage /> },
          { path: "products/create", element: <ZeelyProductCreatePage /> },

          // ── Settings ─────────────────────────────────────────────────
          {
            path: "settings",
            element: (
              <SettingsLayout>
                <Outlet />
              </SettingsLayout>
            ),
            children: [
              { index: true, element: <Navigate to="organization" replace /> },
              { path: "organization", element: <OrganizationSettingsPage /> },
              { path: "team", element: <TeamSettingsPage /> },
              { path: "billing", element: <SettingsProfilePage /> },
              { path: "profile", element: <Navigate to="/settings/billing" replace /> },
              { path: "general", element: <SettingsGeneralPage /> },
              { path: "ai-providers", element: <Navigate to="/settings/organization" replace /> },
              {
                path: "admin",
                element: (
                  <AdminLayout>
                    <Outlet />
                  </AdminLayout>
                ),
                children: [
                  { index: true, element: <AdminPage /> },
                  { path: "pipeline-inspector", element: <AdminPipelineInspectorPage /> },
                  { path: "tts-voices", element: <AdminTtsVoicesPage /> },
                ],
              },
              { path: "agent-logs", element: <AgentLogsPage /> },
              { path: "agent-logs/:chatId", element: <AgentLogDetailPage /> },
              { path: "agent-logs/pipelines", element: <AgentLogsPipelinesPage /> },
              // /settings/agents/* → /console/* (Phase 3.5 migration)
              { path: "agents", element: <Navigate to="/console/agents" replace /> },
              { path: "agents/sessions", element: <Navigate to="/console/sessions" replace /> },
              { path: "agents/sessions/:sessionId", element: <LegacySessionRedirect /> },
              { path: "agents/playground", element: <Navigate to="/console/playground" replace /> },
              { path: "agent-logs/admin", element: <Navigate to="/settings/admin" replace /> },
              {
                path: "agent-logs/admin/pipeline-inspector",
                element: <Navigate to="/settings/admin/pipeline-inspector" replace />,
              },
              {
                path: "agent-logs/admin/tts-voices",
                element: <Navigate to="/settings/admin/tts-voices" replace />,
              },
            ],
          },

          // Phase 26 — `/console/*` moved OUT of AppLayout into its own
          // brand shell. See the AigentiseConsoleMount sibling block below.
        ],
      },

      // ── Talking Avatar / Lipsync flow (focused chrome, no app sidebar) ──
      // Mirrors Zeely's `/create-lipsync-video/*` shell. Runs OUTSIDE
      // `AppLayout` so the page owns the full viewport — only the LipsyncLayout
      // top bar (stepper + credit badge + X close) is rendered.
      {
        element: (
          <ProtectedRoute>
            <AdsAuralisScope>
              <Suspense fallback={<PageLoading />}>
                <LipsyncLayout />
              </Suspense>
            </AdsAuralisScope>
          </ProtectedRoute>
        ),
        children: [{ path: "video-ads/talking-avatar/:step", element: <TalkingAvatarPage /> }],
      },

      // ── Aigentise console (Phase 26) ────────────────────────────────────
      // Staff-only. Renders OUTSIDE the WiserStudio AppLayout/AppShell so the
      // Aigentise brand surface (own sidebar, scoped palette, light/dark toggle)
      // owns the full viewport. All `/console/*` URLs stay byte-identical.
      {
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoading />}>
              <AigentiseConsoleMount>
                <Outlet />
              </AigentiseConsoleMount>
            </Suspense>
          </ProtectedRoute>
        ),
        children: [
          {
            path: "console",
            element: (
              <Suspense fallback={<PageLoading />}>
                <ConsoleLayout />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleIndexPage />
                  </Suspense>
                ),
              },
              {
                path: "agents",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleAgentsListPage />
                  </Suspense>
                ),
              },
              {
                path: "agents/new",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleNewAgentPage />
                  </Suspense>
                ),
              },
              {
                path: "agents/:agentName",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleAgentDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "agents/:agentName/edit",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleEditAgentPage />
                  </Suspense>
                ),
              },
              {
                // Phase 24-2c. Renders the Workbench layout shell. The version
                // segment is optional — when absent, the page resolves the
                // landing version (latest draft → active → staging) and writes
                // it back into the URL.
                path: "agents/:agentName/workbench",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleWorkbenchPage />
                  </Suspense>
                ),
              },
              {
                path: "agents/:agentName/workbench/:versionId",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleWorkbenchPage />
                  </Suspense>
                ),
              },
              {
                path: "playground",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsolePlaygroundLandingPage />
                  </Suspense>
                ),
              },
              {
                path: "playground/:agentName",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleAgentPlaygroundPage />
                  </Suspense>
                ),
              },
              {
                path: "playground/:agentName/:versionId",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleVersionPinnedPlaygroundPage />
                  </Suspense>
                ),
              },
              {
                path: "sessions",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleSessionsListPage />
                  </Suspense>
                ),
              },
              {
                path: "sessions/:sessionId",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleSessionDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "units",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleUnitsPage />
                  </Suspense>
                ),
              },
              {
                path: "units/:unitName",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleUnitDetailPage />
                  </Suspense>
                ),
              },
              // Phase 13-4 — tool playground list.
              {
                path: "tools",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleToolsListPage />
                  </Suspense>
                ),
              },
              // Phase 13-5 — tool playground detail.
              {
                path: "tools/:toolName",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleToolDetailPage />
                  </Suspense>
                ),
              },
              // Phase 20-11 — triggers surface.
              {
                path: "triggers",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleTriggersListPage />
                  </Suspense>
                ),
              },
              {
                path: "triggers/new",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleNewTriggerPage />
                  </Suspense>
                ),
              },
              {
                path: "triggers/:triggerId",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleTriggerDetailPage />
                  </Suspense>
                ),
              },
              // Phase 20-12 — global safety dashboard.
              {
                path: "safety",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleSafetyPage />
                  </Suspense>
                ),
              },
              // Phase 14-6 — datasets surface.
              {
                path: "datasets",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleDatasetsListPage />
                  </Suspense>
                ),
              },
              {
                path: "datasets/:datasetId",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleDatasetDetailPage />
                  </Suspense>
                ),
              },
              // Phase 14-7 — evaluation run detail page.
              {
                path: "evaluations/:runId",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleEvaluationDetailPage />
                  </Suspense>
                ),
              },
              // Phase 19-5 — memory + knowledge-base console.
              {
                path: "memory",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleMemoryPage />
                  </Suspense>
                ),
              },
              {
                path: "knowledge-bases",
                element: (
                  <Suspense fallback={<PageLoading />}>
                    <ConsoleKnowledgeBasesPage />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },

      // ── Standalone pages ───────────────────────────────────────────────
      { path: "agents-preview", element: <AgentsPreviewPage /> },
      { path: "logout", element: <LogoutPage /> },
      { path: "auth/google/callback", element: <GoogleOAuthCallbackPage /> },

      // ── Catch-all ──────────────────────────────────────────────────────
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);
